/**
 * Berekent de score voor een Qwixx scorekaart
 *
 * @param {Object} playerScorecard - De scorekaart van een speler met gemarkeerde nummers per rij
 * @param {Object} playerClosedRows - Object dat aangeeft welke rijen zijn afgesloten
 * @param {Object} playerRowClosers - Object met arrays van spelersnamen die elke rij hebben afgesloten
 * @param {string} playerName - Naam van de speler om te controleren of ze de rij hebben afgesloten
 * @returns {Object} - Object met totale score en uitsplitsing per rij
 */
export const calculateScore = (playerScorecard, playerClosedRows = {}, playerRowClosers = {}, playerName = null) => {
  let score = 0;
  const breakdown = {
    red: 0,
    yellow: 0,
    green: 0,
    blue: 0,
    penalties: 0,
    total: 0
  };

  // Add scores for each row
  ['red', 'yellow', 'green', 'blue'].forEach(color => {
    // Base count of marked numbers in the row - CRITICAL: Check count FIRST
    const count = playerScorecard[color].length;

    // Early exit if no marks - prevents phantom points bug
    if (count === 0) {
      breakdown[color] = 0;
      return; // Skip to next color
    }

    // Check if the row is closed
    // Lock bonus is ONLY given to players who are in the rowClosers array for this color
    const lastNumber = color === 'red' || color === 'yellow' ? 12 : 2;

    // Debug logging to trace the issue
    const hasLastNumber = playerScorecard[color].includes(lastNumber);
    const hasEnoughMarks = count >= 6;
    const didPlayerCloseRow = playerRowClosers[color]?.includes(playerName) || false;

    console.log(`[UTIL] [SCORE] DEBUG Row ${color}:`, {
      playerName: playerName,
      markedNumbers: playerScorecard[color],
      count: count,
      lastNumber: lastNumber,
      hasLastNumber: hasLastNumber,
      hasEnoughMarks: hasEnoughMarks,
      rowClosers: playerRowClosers[color] || [],
      didPlayerCloseRow: didPlayerCloseRow
    });

    // Only give lock bonus if this player actually closed the row
    const isRowClosed = didPlayerCloseRow && hasEnoughMarks;

    // Calculate actual count including lock bonus (only after validating count > 0)
    const actualCount = isRowClosed ? count + 1 : count;
    const rowScore = (actualCount * (actualCount + 1)) / 2; // Sum formula: n(n+1)/2

    score += rowScore;
    breakdown[color] = rowScore;

    // Log the calculation for debugging
    if (isRowClosed) {
      console.log(`[UTIL] [SCORE] Row ${color}: ${count} marks (including last number) + 1 lock bonus = ${actualCount} total = ${rowScore} points`);
    } else {
      console.log(`[UTIL] [SCORE] Row ${color}: ${count} marks = ${rowScore} points (NOT CLOSED)`);
    }
  });

  // Subtract penalties
  // Ensure penalties is always 0 or a negative number
  const penaltyPoints = playerScorecard.penalties > 0 ? playerScorecard.penalties * 5 : 0;
  score -= penaltyPoints;
  breakdown.penalties = penaltyPoints > 0 ? -penaltyPoints : 0; // Ensure always negative or zero
  breakdown.total = score;

  console.log(`[UTIL] [SCORE] Total: ${score} points (red=${breakdown.red}, yellow=${breakdown.yellow}, green=${breakdown.green}, blue=${breakdown.blue}, penalties=${breakdown.penalties})`);

  return { score, breakdown };
};

/**
 * Controleer of een rij gesloten kan worden
 * 
 * @param {string} row - De kleur van de rij ('red', 'yellow', 'green', 'blue')
 * @param {Array} markedNumbers - Array met gemarkeerde nummers in de rij
 * @returns {boolean} - True als de rij gesloten kan worden
 */
export const canCloseRow = (row, markedNumbers) => {
  // Laatste nummer voor rood/geel is 12, voor groen/blauw is het 2
  const lastNumber = row === 'red' || row === 'yellow' ? 12 : 2;

  // Om een rij te sluiten, moet het laatste nummer gemarkeerd zijn
  // en er moeten minstens 6 nummers gemarkeerd zijn (5 regular + last number)
  // This matches the scoring logic which checks count >= 6
  return markedNumbers.includes(lastNumber) && markedNumbers.length >= 6;
};

const scoreCalculationUtils = {
  calculateScore,
  canCloseRow
};

export default scoreCalculationUtils; 